import { QueryClient } from "react-query";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import {
  BookingScheduleDto,
  BookingScheduleInterface
} from "../../../../features/Booking-Form/typings/booking.types";
import {
  bookableBookingObjectsQueryKey,
  fetchBookableBookingObjects
} from "../../../../hooks/Remote/Schedule/useRemoteFetchBookableBookingObjects";
import { ScheduleMode } from "../../../../features/Schedule/partial/ScheduleModeToggle.partial";
import { BookableBookingObjectDto } from "../../../../features/Schedule/typings/resource-calendar.types";
import { colleaguePlaceSchedules } from "../../../../features/Schedule/functions/handleScheduleData.functions";
import type { PlaceScheduleTypeVisibility } from "../../../../features/Schedule/utils/resource-booking-schedule.utils";
import { mergeColleagueScheduleSources } from "./schedule-excel-export.functions";
import { dedupeBookableBookingObjects } from "./schedule-excel-export.functions";

export type ColleagueScheduleExcelExportFetchConfig = {
  scheduleMode: ScheduleMode.Colleague;
  userIds: string[];
  /** Workplace meta or parking/EV placeTypes — gates `/booking-schedule/place`. */
  placeScheduleEnabled: boolean;
  /** Filters mixed place-schedule rows after fetch (workplace meta / parking-EV license). */
  placeScheduleVisibility: PlaceScheduleTypeVisibility;
  hasConferenceLicense: boolean;
  hasVehicleLicense: boolean;
  hasResourceLicense: boolean;
};

export type ResourceScheduleExcelExportFetchConfig = {
  scheduleMode: ScheduleMode.Resource;
  resourceBookingType: BookingType | null;
  resourceAvailableTypes: BookingType[];
};

export type ScheduleExcelExportFetchConfig =
  | ColleagueScheduleExcelExportFetchConfig
  | ResourceScheduleExcelExportFetchConfig;

export type ColleagueScheduleExcelExportFetchDeps = {
  fetchPlaceSchedule: (dto: BookingScheduleDto) => Promise<BookingScheduleInterface[]>;
  fetchNoPlaceZoneSchedule: (dto: BookingScheduleDto) => Promise<BookingScheduleInterface[]>;
  fetchVehicleSchedule: (dto: BookingScheduleDto) => Promise<BookingScheduleInterface[]>;
  fetchResourceSchedule: (dto: BookingScheduleDto) => Promise<BookingScheduleInterface[]>;
};

export type ResourceScheduleExcelExportFetchDeps = {
  queryClient: QueryClient;
  fetchResourceScheduleByObject: (params: {
    bookingInventoryId: number;
    bookingType: BookingType;
    startDate: string;
    endDate: string;
  }) => Promise<BookingScheduleInterface[]>;
};

export type ScheduleExcelExportFetchDeps = {
  colleague: ColleagueScheduleExcelExportFetchDeps;
  resource: ResourceScheduleExcelExportFetchDeps;
};

function bookingScheduleDto(
  userIds: string[],
  startDate: string,
  endDate: string
): BookingScheduleDto {
  return { userIds, startDate, endDate };
}

async function fetchBookableObjectsForTypes(
  queryClient: QueryClient,
  startDate: string,
  endDate: string,
  bookingTypes: BookingType[]
): Promise<BookableBookingObjectDto[]> {
  const lists = await Promise.all(
    bookingTypes.map(bookingType =>
      queryClient.fetchQuery(
        bookableBookingObjectsQueryKey({ startDate, endDate, bookingType, includeInactive: true }),
        () =>
          fetchBookableBookingObjects({ startDate, endDate, bookingType, includeInactive: true })
      )
    )
  );
  return dedupeBookableBookingObjects(lists.flat());
}

export async function fetchColleagueSchedulesForExcelExport(
  config: ColleagueScheduleExcelExportFetchConfig,
  period: { startDate: string; endDate: string },
  deps: ColleagueScheduleExcelExportFetchDeps
): Promise<BookingScheduleInterface[]> {
  const dto = bookingScheduleDto(config.userIds, period.startDate, period.endDate);

  const [place, noPlace, vehicle, resource] = await Promise.all([
    config.placeScheduleEnabled ? deps.fetchPlaceSchedule(dto) : Promise.resolve(undefined),
    config.hasConferenceLicense ? deps.fetchNoPlaceZoneSchedule(dto) : Promise.resolve(undefined),
    config.hasVehicleLicense ? deps.fetchVehicleSchedule(dto) : Promise.resolve(undefined),
    config.hasResourceLicense ? deps.fetchResourceSchedule(dto) : Promise.resolve(undefined)
  ]);

  return mergeColleagueScheduleSources(
    colleaguePlaceSchedules(place, config.placeScheduleVisibility),
    noPlace,
    vehicle,
    resource
  );
}

export async function fetchResourceSchedulesForExcelExport(
  config: ResourceScheduleExcelExportFetchConfig,
  period: { startDate: string; endDate: string },
  deps: ResourceScheduleExcelExportFetchDeps
): Promise<BookingScheduleInterface[]> {
  const types =
    config.resourceAvailableTypes.length > 0
      ? config.resourceAvailableTypes
      : [config.resourceBookingType ?? BookingType.WORKPLACE];

  const bookableObjects = await fetchBookableObjectsForTypes(
    deps.queryClient,
    period.startDate,
    period.endDate,
    types
  );

  if (bookableObjects.length === 0) {
    return [];
  }

  const scheduleLists = await Promise.all(
    bookableObjects.map(obj =>
      deps.fetchResourceScheduleByObject({
        bookingInventoryId: obj.bookingInventoryId,
        bookingType: obj.bookingType,
        startDate: period.startDate,
        endDate: period.endDate
      })
    )
  );

  return scheduleLists.flat();
}

export async function fetchSchedulesForExcelExport(
  config: ScheduleExcelExportFetchConfig,
  period: { startDate: string; endDate: string },
  deps: ScheduleExcelExportFetchDeps
): Promise<BookingScheduleInterface[]> {
  if (config.scheduleMode === ScheduleMode.Colleague) {
    return fetchColleagueSchedulesForExcelExport(config, period, deps.colleague);
  }

  return fetchResourceSchedulesForExcelExport(config, period, deps.resource);
}
